/**
 * 실시간 알림 패널 — Header 배치 (배지 + 드롭다운)
 */

import React, { useCallback } from 'react';
import { Badge, Button, Dropdown, Empty, List, Typography } from 'antd';
import { BellOutlined } from '@ant-design/icons';

import { useNotificationStore } from '../../stores/NotificationStore';
import { css } from '@shared/utils/omitUndefined';
import styles from './NotificationPanel.module.css';

function formatReceivedAt(date: Date): string {
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NotificationPanel(): React.ReactElement {
  const { notifications, unreadCount, markAllRead, markRead } = useNotificationStore();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && unreadCount > 0) {
        markAllRead();
      }
    },
    [markAllRead, unreadCount]
  );

  const dropdownContent = (
    <div className={css(styles.dropdown)} role="region" aria-label="실시간 알림 목록">
      {notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="새 알림이 없습니다"
          className={css(styles.emptyWrap)}
        />
      ) : (
        <List
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              data-priority={item.priority}
              className={css(
                [styles.listItem, item.read && styles.listItemRead].filter(Boolean).join(' ')
              )}
              onClick={() => markRead(item.id)}
            >
              <List.Item.Meta
                title={
                  <Typography.Text strong={!item.read} ellipsis>
                    {item.title ?? item.type}
                  </Typography.Text>
                }
                description={
                  <>
                    <Typography.Paragraph
                      ellipsis={{ rows: 2 }}
                      className={css(styles.messageParagraph)}
                    >
                      {item.message}
                    </Typography.Paragraph>
                    <Typography.Text type="secondary" className={css(styles.receivedAt)}>
                      {formatReceivedAt(item.receivedAt)}
                    </Typography.Text>
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
      onOpenChange={handleOpenChange}
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined />}
          aria-label={unreadCount ? `실시간 알림 ${unreadCount}건` : '실시간 알림'}
          title="신경 신호 알림"
        />
      </Badge>
    </Dropdown>
  );
}

export default NotificationPanel;
